var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/data-table.tsx
import React8, { useState as useState4, useMemo } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Download } from "lucide-react";

// src/renderers.tsx
import React from "react";
import { formatMoney } from "@repo/utils";
var DEFAULT_LOCALE = "en-IN";
function toSafePaise(value) {
  const num = Number(value);
  return value == null || Number.isNaN(num) ? 0 : num;
}
__name(toSafePaise, "toSafePaise");
function formatDate(value) {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString(DEFAULT_LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
__name(formatDate, "formatDate");
function formatDatetime(value) {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleDateString(DEFAULT_LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
__name(formatDatetime, "formatDatetime");
var STATUS_COLORS = {
  success: "bg-success-bg text-success-text",
  warning: "bg-warning-bg text-warning-text",
  danger: "bg-danger-bg text-danger-text",
  info: "bg-info-bg text-info-text"
};
var STATUS_DOTS = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-info-500"
};
var DEFAULT_RENDERERS = {
  text: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("span", null, String(value ?? "")), "text"),
  code: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("span", {
    className: "font-mono text-xs text-text-primary"
  }, String(value ?? "")), "code"),
  link: /* @__PURE__ */ __name((value, meta) => {
    const href = meta?.hrefTemplate ? String(meta.hrefTemplate).replace("{value}", String(value ?? "")) : String(value ?? "");
    return /* @__PURE__ */ React.createElement("a", {
      href,
      className: "text-accent-500 hover:underline font-medium"
    }, String(value ?? ""));
  }, "link"),
  numeric: /* @__PURE__ */ __name((value) => {
    const num = toSafePaise(value);
    return /* @__PURE__ */ React.createElement("span", {
      className: "text-right tnum"
    }, num.toLocaleString(DEFAULT_LOCALE));
  }, "numeric"),
  money: /* @__PURE__ */ __name((value) => {
    const paise = toSafePaise(value);
    return /* @__PURE__ */ React.createElement("span", {
      className: "tnum font-medium"
    }, formatMoney(paise));
  }, "money"),
  date: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("span", {
    className: "text-sm text-text-secondary"
  }, value ? formatDate(String(value)) : "\u2014"), "date"),
  datetime: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("span", {
    className: "text-sm text-text-secondary"
  }, value ? formatDatetime(String(value)) : "\u2014"), "datetime"),
  status: /* @__PURE__ */ __name((value, meta) => {
    const label = String(value ?? "");
    const colorKey = meta?.colorMap?.[label] ?? "info";
    const bgClass = STATUS_COLORS[colorKey] ?? STATUS_COLORS.info;
    const dotClass = STATUS_DOTS[colorKey] ?? STATUS_DOTS.info;
    return /* @__PURE__ */ React.createElement("span", {
      className: `inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${bgClass}`
    }, /* @__PURE__ */ React.createElement("span", {
      className: `w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`,
      "aria-hidden": "true"
    }), label);
  }, "status"),
  boolean: /* @__PURE__ */ __name((value) => {
    const isTrue = Boolean(value);
    return /* @__PURE__ */ React.createElement("span", {
      className: isTrue ? "text-success-500" : "text-text-muted"
    }, isTrue ? "\u2713" : "\u2014");
  }, "boolean"),
  email: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("a", {
    href: `mailto:${String(value ?? "")}`,
    className: "text-accent-500 hover:underline text-sm"
  }, String(value ?? "")), "email"),
  phone: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("a", {
    href: `tel:${String(value ?? "")}`,
    className: "text-accent-500 hover:underline text-sm font-mono"
  }, String(value ?? "")), "phone"),
  avatar: /* @__PURE__ */ __name((value, meta) => {
    const text = String(value ?? "");
    const subtext = meta?.subtext ? String(meta.subtext) : void 0;
    const initials = text.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase();
    return /* @__PURE__ */ React.createElement("div", {
      className: "flex items-center gap-2"
    }, /* @__PURE__ */ React.createElement("span", {
      className: "w-7 h-7 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center font-bold text-xs flex-shrink-0"
    }, initials || "?"), /* @__PURE__ */ React.createElement("div", {
      className: "flex flex-col min-w-0"
    }, /* @__PURE__ */ React.createElement("span", {
      className: "font-medium text-text-primary text-sm truncate"
    }, text), subtext && /* @__PURE__ */ React.createElement("span", {
      className: "text-xs text-text-secondary truncate"
    }, subtext)));
  }, "avatar"),
  progress: /* @__PURE__ */ __name((value) => {
    const num = Math.min(100, Math.max(0, Number(value) || 0));
    return /* @__PURE__ */ React.createElement("div", {
      className: "flex items-center gap-2 w-full max-w-[120px]"
    }, /* @__PURE__ */ React.createElement("div", {
      className: "flex-1 bg-surface-3 h-2 rounded-full overflow-hidden border border-border"
    }, /* @__PURE__ */ React.createElement("div", {
      className: "bg-accent-500 h-full transition-all duration-300",
      style: {
        width: `${num}%`
      }
    })), /* @__PURE__ */ React.createElement("span", {
      className: "text-xs font-mono text-text-secondary tnum"
    }, num, "%"));
  }, "progress"),
  badge: /* @__PURE__ */ __name((value) => /* @__PURE__ */ React.createElement("span", {
    className: "inline-flex items-center px-2 py-0.5 rounded-[4px] text-xs font-medium bg-surface-2 text-text-primary border border-border"
  }, String(value ?? "")), "badge"),
  "tag-list": /* @__PURE__ */ __name((value) => {
    const tags = Array.isArray(value) ? value : String(value ?? "").split(",").filter(Boolean);
    if (tags.length === 0) return /* @__PURE__ */ React.createElement("span", {
      className: "text-text-muted"
    }, "\u2014");
    return /* @__PURE__ */ React.createElement("div", {
      className: "flex flex-wrap gap-1"
    }, tags.map((tag, idx) => /* @__PURE__ */ React.createElement("span", {
      key: idx,
      className: "inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-surface-2 text-text-secondary border border-border"
    }, String(tag).trim())));
  }, "tag-list")
};
function getCellValue(row, column) {
  if (typeof column.accessor === "function") {
    return column.accessor(row);
  }
  return row[column.accessor];
}
__name(getCellValue, "getCellValue");
function renderCell(row, column, customRenderers) {
  if (customRenderers?.[column.id]) {
    return customRenderers[column.id](row);
  }
  const value = getCellValue(row, column);
  if (!column.renderType) {
    return /* @__PURE__ */ React.createElement("span", null, String(value ?? ""));
  }
  const renderer = DEFAULT_RENDERERS[column.renderType];
  if (!renderer) {
    return /* @__PURE__ */ React.createElement("span", null, String(value ?? ""));
  }
  return renderer(value, column.renderMeta);
}
__name(renderCell, "renderCell");

// src/filter-bar.tsx
import React2 from "react";
import { cn } from "@repo/utils";
function FilterBar({ filters, values, onChange, onReset, className }) {
  const hasActiveFilters = filters.some((f) => values[f.id]);
  if (filters.length === 0) return null;
  return /* @__PURE__ */ React2.createElement("div", {
    className: cn("flex flex-wrap gap-3 items-center", className)
  }, filters.map((filter) => {
    if (filter.type === "select") {
      return /* @__PURE__ */ React2.createElement("div", {
        key: filter.id,
        className: "flex flex-col gap-0.5"
      }, /* @__PURE__ */ React2.createElement("label", {
        className: "text-xs font-bold text-text-secondary uppercase tracking-wide"
      }, filter.label), /* @__PURE__ */ React2.createElement("select", {
        value: values[filter.id] ?? "",
        onChange: /* @__PURE__ */ __name((e) => {
          onChange(filter.id, e.target.value || void 0);
        }, "onChange"),
        className: "h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
      }, /* @__PURE__ */ React2.createElement("option", {
        value: ""
      }, filter.placeholder ?? `All ${filter.label}`), filter.options?.map((opt) => /* @__PURE__ */ React2.createElement("option", {
        key: opt.value,
        value: opt.value
      }, opt.label))));
    }
    if (filter.type === "text") {
      return /* @__PURE__ */ React2.createElement("div", {
        key: filter.id,
        className: "flex flex-col gap-0.5"
      }, /* @__PURE__ */ React2.createElement("label", {
        className: "text-xs font-bold text-text-secondary uppercase tracking-wide"
      }, filter.label), /* @__PURE__ */ React2.createElement("input", {
        type: "text",
        value: values[filter.id] ?? "",
        onChange: /* @__PURE__ */ __name((e) => onChange(filter.id, e.target.value || void 0), "onChange"),
        placeholder: filter.placeholder,
        className: "h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-500"
      }));
    }
    return null;
  }), hasActiveFilters && /* @__PURE__ */ React2.createElement("button", {
    onClick: onReset,
    className: "h-9 px-3 text-xs font-medium text-accent-500 hover:text-accent-400 bg-transparent hover:bg-surface-2 rounded-[6px] transition-colors self-end"
  }, "Clear"));
}
__name(FilterBar, "FilterBar");

// src/filter-chips.tsx
import React3 from "react";
import { X } from "lucide-react";
import { cn as cn2 } from "@repo/utils";
function FilterChips({ filters, values, onRemove, onClearAll, className }) {
  const activeFilters = filters.filter((f) => Boolean(values[f.id]));
  if (activeFilters.length === 0) return null;
  return /* @__PURE__ */ React3.createElement("div", {
    className: cn2("flex flex-wrap items-center gap-1.5 pt-1 text-xs", className)
  }, /* @__PURE__ */ React3.createElement("span", {
    className: "text-text-muted font-medium uppercase tracking-wider text-[10px]"
  }, "Active Filters:"), activeFilters.map((filter) => {
    const val = values[filter.id];
    let displayVal = val;
    if (filter.type === "select" && filter.options) {
      const match = filter.options.find((o) => o.value === val);
      if (match) displayVal = match.label;
    }
    return /* @__PURE__ */ React3.createElement("span", {
      key: filter.id,
      className: "inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px] bg-accent-50 text-accent-700 font-medium border border-accent-200"
    }, /* @__PURE__ */ React3.createElement("span", {
      className: "text-text-secondary font-normal"
    }, filter.label, ":"), /* @__PURE__ */ React3.createElement("span", null, displayVal), /* @__PURE__ */ React3.createElement("button", {
      type: "button",
      onClick: /* @__PURE__ */ __name(() => onRemove(filter.id), "onClick"),
      className: "hover:text-accent-500 rounded p-0.5 transition-colors",
      "aria-label": `Remove filter ${filter.label}`
    }, /* @__PURE__ */ React3.createElement(X, {
      className: "w-3 h-3"
    })));
  }), activeFilters.length > 1 && /* @__PURE__ */ React3.createElement("button", {
    type: "button",
    onClick: onClearAll,
    className: "text-accent-500 hover:text-accent-700 font-medium underline underline-offset-2 px-1 text-xs transition-colors"
  }, "Clear all"));
}
__name(FilterChips, "FilterChips");

// src/search-input.tsx
import React4 from "react";
import { cn as cn3 } from "@repo/utils";
function SearchInput({ value, onChange, placeholder = "Search...", className }) {
  const [local, setLocal] = React4.useState(value);
  const timerRef = React4.useRef(void 0);
  React4.useEffect(() => {
    setLocal(value ?? "");
  }, [
    value
  ]);
  React4.useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);
  const handleChange = /* @__PURE__ */ __name((e) => {
    const next = e.target.value;
    setLocal(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(next);
    }, 300);
  }, "handleChange");
  const handleClear = /* @__PURE__ */ __name(() => {
    setLocal("");
    clearTimeout(timerRef.current);
    onChange("");
  }, "handleClear");
  return /* @__PURE__ */ React4.createElement("div", {
    className: cn3("relative", className)
  }, /* @__PURE__ */ React4.createElement("svg", {
    className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none",
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    "aria-hidden": "true"
  }, /* @__PURE__ */ React4.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
  })), /* @__PURE__ */ React4.createElement("input", {
    type: "text",
    value: local,
    onChange: handleChange,
    placeholder,
    className: "w-full h-9 pl-9 pr-8 text-sm bg-surface-1 border border-border rounded-[6px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-500"
  }), local && /* @__PURE__ */ React4.createElement("button", {
    onClick: handleClear,
    className: "absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-text-muted hover:text-text-secondary rounded-full hover:bg-surface-2",
    "aria-label": "Clear search"
  }, /* @__PURE__ */ React4.createElement("svg", {
    className: "w-3 h-3",
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    "aria-hidden": "true"
  }, /* @__PURE__ */ React4.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M6 18L18 6M6 6l12 12"
  }))));
}
__name(SearchInput, "SearchInput");

// src/column-visibility.tsx
import React5, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Columns, Check } from "lucide-react";
import { cn as cn4 } from "@repo/utils";
function ColumnVisibilityMenu({ columns, hiddenColumns, onToggleColumn, onResetColumns, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [coords, setCoords] = useState({
    top: 0,
    right: 0
  });
  const toggleableColumns = columns.filter((col) => !col.disableToggle);
  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, []);
  const handleToggle = /* @__PURE__ */ __name((e) => {
    e.stopPropagation();
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen(!isOpen);
  }, "handleToggle");
  useEffect(() => {
    if (!isOpen) return;
    updateCoords();
    const handleScrollOrResize = /* @__PURE__ */ __name(() => updateCoords(), "handleScrollOrResize");
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [
    isOpen,
    updateCoords
  ]);
  return /* @__PURE__ */ React5.createElement("div", {
    className: cn4("relative inline-block text-left", className)
  }, /* @__PURE__ */ React5.createElement("button", {
    ref: buttonRef,
    type: "button",
    onClick: handleToggle,
    className: "inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium rounded-[6px] bg-surface-1 border border-border text-text-primary hover:bg-surface-2 focus:outline-none focus:border-border-strong transition-colors",
    "aria-expanded": isOpen,
    "aria-haspopup": "true"
  }, /* @__PURE__ */ React5.createElement(Columns, {
    className: "w-3.5 h-3.5 text-text-secondary"
  }), /* @__PURE__ */ React5.createElement("span", null, "Columns")), isOpen && /* @__PURE__ */ createPortal(/* @__PURE__ */ React5.createElement(React5.Fragment, null, /* @__PURE__ */ React5.createElement("div", {
    className: "fixed inset-0 z-[99998] bg-transparent",
    onClick: /* @__PURE__ */ __name((e) => {
      e.stopPropagation();
      setIsOpen(false);
    }, "onClick")
  }), /* @__PURE__ */ React5.createElement("div", {
    style: {
      top: coords.top,
      right: coords.right
    },
    className: "fixed z-[99999] w-48 rounded-[8px] bg-surface-1 border border-border shadow-xl py-1.5 text-xs text-text-primary animate-in fade-in zoom-in-95 duration-100",
    onClick: /* @__PURE__ */ __name((e) => e.stopPropagation(), "onClick")
  }, /* @__PURE__ */ React5.createElement("div", {
    className: "px-3 py-1 font-semibold text-text-secondary uppercase tracking-wider text-[10px] border-b border-border mb-1 flex items-center justify-between"
  }, /* @__PURE__ */ React5.createElement("span", null, "Toggle Columns"), onResetColumns && /* @__PURE__ */ React5.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name((e) => {
      e.stopPropagation();
      onResetColumns();
    }, "onClick"),
    className: "text-accent-500 hover:underline normal-case font-normal"
  }, "Reset")), /* @__PURE__ */ React5.createElement("div", {
    className: "max-h-56 overflow-y-auto px-1 space-y-0.5"
  }, toggleableColumns.map((col) => {
    const isVisible = !hiddenColumns.has(col.id);
    return /* @__PURE__ */ React5.createElement("button", {
      key: col.id,
      type: "button",
      onClick: /* @__PURE__ */ __name((e) => {
        e.stopPropagation();
        onToggleColumn(col.id);
      }, "onClick"),
      className: "w-full flex items-center justify-between px-2 py-1.5 rounded-[4px] hover:bg-surface-2 transition-colors text-left"
    }, /* @__PURE__ */ React5.createElement("span", {
      className: "truncate"
    }, col.header), isVisible && /* @__PURE__ */ React5.createElement(Check, {
      className: "w-3.5 h-3.5 text-accent-500 flex-shrink-0"
    }));
  })))), document.body));
}
__name(ColumnVisibilityMenu, "ColumnVisibilityMenu");

// src/bulk-toolbar.tsx
import React6, { useState as useState2 } from "react";
import { X as X2, AlertTriangle } from "lucide-react";
import { cn as cn5 } from "@repo/utils";
function BulkToolbar({ selectedCount, selectedRows, selectedIds, bulkActions = [], onClearSelection, className }) {
  const [pendingAction, setPendingAction] = useState2(null);
  if (selectedCount === 0) return null;
  const handleActionClick = /* @__PURE__ */ __name((action) => {
    if (action.requiresConfirmation || action.variant === "destructive") {
      setPendingAction(action);
    } else {
      action.onClick(selectedRows, selectedIds);
    }
  }, "handleActionClick");
  const confirmPendingAction = /* @__PURE__ */ __name(() => {
    if (pendingAction) {
      pendingAction.onClick(selectedRows, selectedIds);
      setPendingAction(null);
    }
  }, "confirmPendingAction");
  return /* @__PURE__ */ React6.createElement(React6.Fragment, null, /* @__PURE__ */ React6.createElement("div", {
    className: cn5("flex items-center justify-between px-3 py-2 bg-surface-3 border border-border-strong rounded-[8px] shadow-sm animate-in fade-in slide-in-from-top-1 duration-150", className)
  }, /* @__PURE__ */ React6.createElement("div", {
    className: "flex items-center gap-2"
  }, /* @__PURE__ */ React6.createElement("span", {
    className: "inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-accent-500 text-white tnum"
  }, selectedCount), /* @__PURE__ */ React6.createElement("span", {
    className: "text-xs font-medium text-text-primary"
  }, "selected")), /* @__PURE__ */ React6.createElement("div", {
    className: "flex items-center gap-2"
  }, bulkActions.map((action, index) => /* @__PURE__ */ React6.createElement("button", {
    key: index,
    type: "button",
    onClick: /* @__PURE__ */ __name(() => handleActionClick(action), "onClick"),
    className: cn5("inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-[6px] transition-colors", action.variant === "destructive" ? "bg-danger-bg text-danger-text hover:bg-danger-500 hover:text-white" : "bg-surface-1 border border-border text-text-primary hover:bg-surface-2")
  }, action.label)), /* @__PURE__ */ React6.createElement("button", {
    type: "button",
    onClick: onClearSelection,
    className: "inline-flex items-center justify-center h-7 w-7 rounded-[6px] text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors ml-1",
    title: "Deselect all"
  }, /* @__PURE__ */ React6.createElement(X2, {
    className: "w-3.5 h-3.5"
  })))), pendingAction && /* @__PURE__ */ React6.createElement("div", {
    className: "fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-150",
    onClick: /* @__PURE__ */ __name(() => setPendingAction(null), "onClick")
  }, /* @__PURE__ */ React6.createElement("div", {
    className: "bg-surface-1 border border-border rounded-[12px] shadow-lg max-w-sm w-full p-5 space-y-4 animate-in zoom-in-95 duration-150",
    onClick: /* @__PURE__ */ __name((e) => e.stopPropagation(), "onClick")
  }, /* @__PURE__ */ React6.createElement("div", {
    className: "flex items-start gap-3"
  }, /* @__PURE__ */ React6.createElement("div", {
    className: cn5("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0", pendingAction.variant === "destructive" ? "bg-danger-bg text-danger-text" : "bg-accent-100 text-accent-700")
  }, /* @__PURE__ */ React6.createElement(AlertTriangle, {
    className: "w-5 h-5"
  })), /* @__PURE__ */ React6.createElement("div", {
    className: "space-y-1"
  }, /* @__PURE__ */ React6.createElement("h4", {
    className: "text-sm font-semibold text-text-primary"
  }, "Confirm Bulk Action"), /* @__PURE__ */ React6.createElement("p", {
    className: "text-xs text-text-secondary"
  }, "Are you sure you want to perform ", /* @__PURE__ */ React6.createElement("strong", null, pendingAction.label), " on ", selectedCount, " selected items?"))), /* @__PURE__ */ React6.createElement("div", {
    className: "flex items-center justify-end gap-2 pt-2 border-t border-border"
  }, /* @__PURE__ */ React6.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPendingAction(null), "onClick"),
    className: "h-8 px-3 text-xs font-medium rounded-[6px] bg-surface-2 border border-border text-text-primary hover:bg-surface-3 transition-colors"
  }, "Cancel"), /* @__PURE__ */ React6.createElement("button", {
    type: "button",
    onClick: confirmPendingAction,
    className: cn5("h-8 px-3 text-xs font-medium rounded-[6px] text-white transition-colors", pendingAction.variant === "destructive" ? "bg-danger-500 hover:bg-danger-600" : "bg-accent-500 hover:bg-accent-600")
  }, "Confirm")))));
}
__name(BulkToolbar, "BulkToolbar");

// src/row-actions.tsx
import React7, { useState as useState3, useRef as useRef2, useEffect as useEffect2, useCallback as useCallback2 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { MoreVertical, AlertTriangle as AlertTriangle2 } from "lucide-react";
import { cn as cn6 } from "@repo/utils";
function RowActions({ actions, row, className }) {
  const [open, setOpen] = useState3(false);
  const [pendingAction, setPendingAction] = useState3(null);
  const buttonRef = useRef2(null);
  const [coords, setCoords] = useState3({
    top: 0,
    right: 0
  });
  const visible = actions.filter((a) => !a.hidden?.(row));
  if (visible.length === 0) return null;
  const updateCoords = useCallback2(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, []);
  const handleToggle = /* @__PURE__ */ __name((e) => {
    e.stopPropagation();
    if (!open) {
      updateCoords();
    }
    setOpen(!open);
  }, "handleToggle");
  useEffect2(() => {
    if (!open) return;
    updateCoords();
    const handleScrollOrResize = /* @__PURE__ */ __name(() => updateCoords(), "handleScrollOrResize");
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [
    open,
    updateCoords
  ]);
  const handleActionClick = /* @__PURE__ */ __name((action) => {
    setOpen(false);
    if (!action.skipDefaultConfirm && (action.requiresConfirmation || action.variant === "destructive")) {
      setPendingAction(action);
    } else {
      action.onClick(row);
    }
  }, "handleActionClick");
  const confirmPendingAction = /* @__PURE__ */ __name(() => {
    if (pendingAction) {
      pendingAction.onClick(row);
      setPendingAction(null);
    }
  }, "confirmPendingAction");
  return /* @__PURE__ */ React7.createElement("div", {
    className: cn6("relative inline-block text-left", className)
  }, /* @__PURE__ */ React7.createElement("button", {
    ref: buttonRef,
    type: "button",
    onClick: handleToggle,
    className: "w-7 h-7 flex items-center justify-center rounded-[6px] text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors",
    "aria-label": "Row actions",
    "aria-expanded": open
  }, /* @__PURE__ */ React7.createElement(MoreVertical, {
    className: "w-4 h-4"
  })), open && /* @__PURE__ */ createPortal2(/* @__PURE__ */ React7.createElement(React7.Fragment, null, /* @__PURE__ */ React7.createElement("div", {
    className: "fixed inset-0 z-[99998] bg-transparent",
    onClick: /* @__PURE__ */ __name((e) => {
      e.stopPropagation();
      setOpen(false);
    }, "onClick")
  }), /* @__PURE__ */ React7.createElement("div", {
    style: {
      top: coords.top,
      right: coords.right
    },
    className: "fixed z-[99999] min-w-[160px] w-max bg-surface-1 border border-border rounded-[8px] shadow-xl py-1 text-xs animate-in fade-in zoom-in-95 duration-100",
    onClick: /* @__PURE__ */ __name((e) => e.stopPropagation(), "onClick")
  }, visible.map((action) => {
    const isDisabled = action.disabled?.(row) ?? false;
    return /* @__PURE__ */ React7.createElement("button", {
      key: action.label,
      type: "button",
      disabled: isDisabled,
      onClick: /* @__PURE__ */ __name((e) => {
        e.stopPropagation();
        if (!isDisabled) handleActionClick(action);
      }, "onClick"),
      className: cn6("w-full px-3.5 py-2 text-left flex items-center justify-between whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium", action.variant === "destructive" ? "text-danger-text hover:bg-danger-bg/40 font-medium" : "text-text-primary hover:bg-surface-2")
    }, /* @__PURE__ */ React7.createElement("span", {
      className: "flex items-center gap-2"
    }, action.icon && /* @__PURE__ */ React7.createElement("span", {
      className: cn6("w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5", action.variant === "destructive" ? "text-danger-text" : "text-text-secondary")
    }, action.icon), /* @__PURE__ */ React7.createElement("span", null, action.label)));
  }))), document.body), pendingAction && /* @__PURE__ */ createPortal2(/* @__PURE__ */ React7.createElement("div", {
    className: "fixed inset-0 z-[999999] bg-black/50 flex items-center justify-center p-4 animate-in fade-in duration-150",
    onClick: /* @__PURE__ */ __name((e) => {
      e.stopPropagation();
      setPendingAction(null);
    }, "onClick")
  }, /* @__PURE__ */ React7.createElement("div", {
    className: "bg-surface-1 border border-border rounded-[12px] shadow-xl max-w-sm w-full p-5 space-y-4 animate-in zoom-in-95 duration-150",
    onClick: /* @__PURE__ */ __name((e) => e.stopPropagation(), "onClick")
  }, /* @__PURE__ */ React7.createElement("div", {
    className: "flex items-start gap-3"
  }, /* @__PURE__ */ React7.createElement("div", {
    className: cn6("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0", pendingAction.variant === "destructive" ? "bg-danger-bg text-danger-text" : "bg-accent-100 text-accent-700")
  }, /* @__PURE__ */ React7.createElement(AlertTriangle2, {
    className: "w-5 h-5"
  })), /* @__PURE__ */ React7.createElement("div", {
    className: "space-y-1"
  }, /* @__PURE__ */ React7.createElement("h4", {
    className: "text-sm font-semibold text-text-primary"
  }, "Confirm ", pendingAction.label), /* @__PURE__ */ React7.createElement("p", {
    className: "text-xs text-text-secondary"
  }, "Are you sure you want to execute ", /* @__PURE__ */ React7.createElement("strong", null, pendingAction.label), "? This action will take effect immediately."))), /* @__PURE__ */ React7.createElement("div", {
    className: "flex items-center justify-end gap-2 pt-2 border-t border-border"
  }, /* @__PURE__ */ React7.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPendingAction(null), "onClick"),
    className: "h-8 px-3 text-xs font-medium rounded-[6px] bg-surface-2 border border-border text-text-primary hover:bg-surface-3 transition-colors"
  }, "Cancel"), /* @__PURE__ */ React7.createElement("button", {
    type: "button",
    onClick: confirmPendingAction,
    className: cn6("h-8 px-3 text-xs font-medium rounded-[6px] text-white transition-colors", pendingAction.variant === "destructive" ? "bg-danger-500 hover:bg-danger-600" : "bg-accent-500 hover:bg-accent-600")
  }, "Confirm")))), document.body));
}
__name(RowActions, "RowActions");

// src/export-csv.ts
function exportToCsv(data, columns, filename = "export.csv") {
  if (!data || data.length === 0) return;
  const visibleColumns = columns.filter((col) => !col.hidden);
  const headers = visibleColumns.map((col) => `"${col.header.replace(/"/g, '""')}"`);
  const rows = [
    headers.join(",")
  ];
  for (const item of data) {
    const rowValues = visibleColumns.map((col) => {
      const val = getCellValue(item, col);
      if (val == null) return '""';
      const strVal = String(val).replace(/"/g, '""');
      return `"${strVal}"`;
    });
    rows.push(rowValues.join(","));
  }
  const csvContent = rows.join("\n");
  const blob = new Blob([
    csvContent
  ], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename.endsWith(".csv") ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
__name(exportToCsv, "exportToCsv");

// src/data-table.tsx
import { cn as cn7 } from "@repo/utils";
function DataTable({ config, list, data: directData, total: directTotal, page: directPage, limit: directLimit, onPageChange: directOnPageChange, onLimitChange: directOnLimitChange, onSearchChange: directOnSearchChange, search: directSearch, loading: directLoading, error: directError, onRetry: directOnRetry, onSortChange, manualSorting: directManualSorting, customRenderers, className, density: initialDensity = "comfortable", showGridLines: initialGridLines = true, stripedRows: initialStripedRows = false, enableDensityToggle = true, enableExportCsv, enableColumnVisibility, selectable = false, selectedIds = [], onSelectionChange, bulkActions, rowActions: propsRowActions, totals, renderExpandedRow: directRenderExpandedRow, pageSizeOptions = [
  10,
  20,
  50,
  100
], toolbarActions }) {
  const data = list ? list.data ?? [] : directData ?? [];
  const meta = list?.meta;
  const total = list ? meta?.total ?? 0 : directTotal ?? 0;
  const limit = list ? meta?.limit ?? config.defaultPageSize ?? 20 : directLimit ?? config.defaultPageSize ?? 20;
  const page = list ? list.page : directPage ?? 1;
  const setPage = list ? list.setPage : directOnPageChange ?? (() => {
  });
  const isLoading = list ? list.isLoading : directLoading ?? false;
  const isError = list ? list.isError : !!directError;
  const error = list ? list.error : directError ? new Error(directError) : null;
  const refetch = list ? list.refetch : directOnRetry ?? (() => {
  });
  const onLimitChange = directOnLimitChange;
  const items = data;
  const [currentDensity, setCurrentDensity] = useState4(initialDensity);
  const [gridLines] = useState4(initialGridLines);
  const [striped] = useState4(initialStripedRows);
  const [hiddenColumns, setHiddenColumns] = useState4(() => {
    const initialHidden = /* @__PURE__ */ new Set();
    config.columns.forEach((col) => {
      if (col.hidden) initialHidden.add(col.id);
    });
    return initialHidden;
  });
  const [expandedRows, setExpandedRows] = useState4(/* @__PURE__ */ new Set());
  const toggleRowExpansion = /* @__PURE__ */ __name((rowId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, "toggleRowExpansion");
  const toggleColumnVisibility = /* @__PURE__ */ __name((columnId) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  }, "toggleColumnVisibility");
  const resetColumnVisibility = /* @__PURE__ */ __name(() => {
    const initialHidden = /* @__PURE__ */ new Set();
    config.columns.forEach((col) => {
      if (col.hidden) initialHidden.add(col.id);
    });
    setHiddenColumns(initialHidden);
  }, "resetColumnVisibility");
  const visibleColumns = useMemo(() => {
    return config.columns.filter((col) => !hiddenColumns.has(col.id));
  }, [
    config.columns,
    hiddenColumns
  ]);
  const tanstackColumns = useMemo(() => {
    return visibleColumns.map((col) => ({
      id: col.id,
      header: col.header,
      accessorFn: /* @__PURE__ */ __name((row) => {
        let val;
        if (typeof col.accessor === "function") {
          val = col.accessor(row);
        } else {
          val = row[col.accessor];
        }
        return val ?? "";
      }, "accessorFn"),
      enableSorting: col.sortable ?? true,
      meta: {
        numeric: col.numeric
      }
    }));
  }, [
    visibleColumns
  ]);
  const initialSort = list?.sort ?? config.defaultSort ?? null;
  const [sorting, setSorting] = useState4(() => initialSort ? [
    {
      id: initialSort.field,
      desc: initialSort.dir === "desc"
    }
  ] : []);
  React8.useEffect(() => {
    if (list?.sort) {
      setSorting([
        {
          id: list.sort.field,
          desc: list.sort.dir === "desc"
        }
      ]);
    } else if (list && !list.sort) {
      setSorting([]);
    }
  }, [
    list?.sort
  ]);
  const handleSortingChange = /* @__PURE__ */ __name((updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    setSorting(next);
    const sortObj = next.length > 0 ? {
      field: next[0].id,
      dir: next[0].desc ? "desc" : "asc"
    } : null;
    if (list?.setSort) {
      list.setSort(sortObj);
    }
    if (onSortChange) {
      onSortChange(sortObj);
    }
  }, "handleSortingChange");
  const table = useReactTable({
    data: items,
    columns: tanstackColumns,
    state: {
      sorting
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: directManualSorting ?? false,
    pageCount: Math.ceil(total / limit)
  });
  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const getItemId = /* @__PURE__ */ __name((item, index) => {
    const rec = item;
    return String(rec.id || rec.contactNumber || rec.code || index);
  }, "getItemId");
  const selectedRows = useMemo(() => {
    return items.filter((item, index) => selectedIds.includes(getItemId(item, index)));
  }, [
    items,
    selectedIds
  ]);
  const allPageIds = useMemo(() => {
    return items.map((item, index) => getItemId(item, index));
  }, [
    items
  ]);
  const isAllSelected = items.length > 0 && allPageIds.every((id) => selectedIds.includes(id));
  const handleSelectAllToggle = /* @__PURE__ */ __name((checked) => {
    if (!onSelectionChange) return;
    if (checked) {
      const merged = Array.from(/* @__PURE__ */ new Set([
        ...selectedIds,
        ...allPageIds
      ]));
      onSelectionChange(merged);
    } else {
      onSelectionChange(selectedIds.filter((id) => !allPageIds.includes(id)));
    }
  }, "handleSelectAllToggle");
  const renderExpandedRow = directRenderExpandedRow ?? config.renderExpandedRow;
  const hasExpandedRow = Boolean(renderExpandedRow);
  const allowColumnVisibility = enableColumnVisibility ?? config.enableColumnVisibility ?? true;
  const allowExportCsv = enableExportCsv ?? config.enableExportCsv ?? true;
  const mergedBulkActions = bulkActions ?? config.bulkActions ?? [];
  const mergedRowActions = propsRowActions ?? config.rowActions ?? [];
  const hasRowActions = mergedRowActions.length > 0;
  const isSearchable = config.searchable ?? Boolean(list?.search || directSearch || directOnSearchChange);
  const searchValue = list ? list.search : directSearch ?? "";
  const setSearchValue = list ? list.setSearch : directOnSearchChange ?? (() => {
  });
  const isFetching = list ? list.isFetching ?? false : false;
  const showInitialLoading = isLoading && items.length === 0;
  if (showInitialLoading) {
    return /* @__PURE__ */ React8.createElement(TableSkeleton, {
      columns: visibleColumns.length + (selectable ? 1 : 0) + (hasExpandedRow ? 1 : 0) + (hasRowActions ? 1 : 0)
    });
  }
  if (isError) {
    return /* @__PURE__ */ React8.createElement("div", {
      className: "flex flex-col items-center justify-center gap-3 p-8 text-center bg-surface-1 border border-border rounded-[10px]"
    }, /* @__PURE__ */ React8.createElement("p", {
      className: "text-sm font-medium text-danger-text"
    }, error?.message ?? "Failed to load data"), /* @__PURE__ */ React8.createElement("button", {
      type: "button",
      onClick: /* @__PURE__ */ __name(() => refetch(), "onClick"),
      className: "inline-flex items-center justify-center h-8 px-3 text-xs font-medium rounded-[6px] bg-surface-2 border border-border-strong text-text-primary hover:bg-surface-3 transition-colors"
    }, "Retry"));
  }
  return /* @__PURE__ */ React8.createElement("div", {
    className: cn7("flex flex-col gap-3", className)
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-wrap items-center justify-between gap-3 bg-surface-1 p-2.5 rounded-[10px] border border-border"
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-wrap items-center gap-3 flex-1 min-w-[280px]"
  }, isSearchable && /* @__PURE__ */ React8.createElement(SearchInput, {
    value: searchValue,
    onChange: setSearchValue,
    placeholder: `Search ${config.emptyStateLabel ?? config.resource}...`,
    className: "w-64"
  }), list?.filters && config.filters && config.filters.length > 0 && /* @__PURE__ */ React8.createElement(FilterBar, {
    filters: config.filters,
    values: list.filters,
    onChange: list.setFilter,
    onReset: list.resetFilters
  })), /* @__PURE__ */ React8.createElement("div", {
    className: "flex items-center gap-2 ml-auto"
  }, toolbarActions, enableDensityToggle && /* @__PURE__ */ React8.createElement("div", {
    className: "inline-flex items-center rounded-[6px] border border-border bg-surface-2 p-0.5 text-xs"
  }, /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setCurrentDensity("comfortable"), "onClick"),
    className: cn7("px-2 py-1 rounded-[4px] font-medium transition-colors", currentDensity === "comfortable" ? "bg-surface-1 text-text-primary shadow-xs" : "text-text-secondary hover:text-text-primary"),
    title: "Comfortable Density"
  }, "Normal"), /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setCurrentDensity("compact"), "onClick"),
    className: cn7("px-2 py-1 rounded-[4px] font-medium transition-colors", currentDensity === "compact" ? "bg-surface-1 text-text-primary shadow-xs" : "text-text-secondary hover:text-text-primary"),
    title: "Compact Density"
  }, "Compact"), /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setCurrentDensity("dense"), "onClick"),
    className: cn7("px-2 py-1 rounded-[4px] font-medium transition-colors", currentDensity === "dense" ? "bg-surface-1 text-text-primary shadow-xs" : "text-text-secondary hover:text-text-primary"),
    title: "High Density (Monospace)"
  }, "Dense")), allowColumnVisibility && /* @__PURE__ */ React8.createElement(ColumnVisibilityMenu, {
    columns: config.columns,
    hiddenColumns,
    onToggleColumn: toggleColumnVisibility,
    onResetColumns: resetColumnVisibility
  }), allowExportCsv && items.length > 0 && /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => exportToCsv(items, config.columns, config.exportFileName ?? config.resource), "onClick"),
    className: "inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium rounded-[6px] bg-surface-1 border border-border text-text-primary hover:bg-surface-2 focus:outline-none transition-colors",
    title: "Export CSV"
  }, /* @__PURE__ */ React8.createElement(Download, {
    className: "w-3.5 h-3.5 text-text-secondary"
  }), /* @__PURE__ */ React8.createElement("span", null, "Export")))), list?.filters && config.filters && /* @__PURE__ */ React8.createElement(FilterChips, {
    filters: config.filters,
    values: list.filters,
    onRemove: /* @__PURE__ */ __name((key) => list.setFilter(key, void 0), "onRemove"),
    onClearAll: list.resetFilters
  }), selectable && selectedIds.length > 0 && /* @__PURE__ */ React8.createElement(BulkToolbar, {
    selectedCount: selectedIds.length,
    selectedRows,
    selectedIds,
    bulkActions: mergedBulkActions,
    onClearSelection: /* @__PURE__ */ __name(() => onSelectionChange?.([]), "onClearSelection")
  }), items.length === 0 ? /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-col items-center justify-center gap-2 p-12 text-center bg-surface-1 border border-border rounded-[10px]"
  }, /* @__PURE__ */ React8.createElement("p", {
    className: "text-sm font-semibold text-text-primary"
  }, "No ", config.emptyStateLabel ?? config.resource, " found"), /* @__PURE__ */ React8.createElement("p", {
    className: "text-xs text-text-secondary"
  }, config.emptyStateDescription ?? "Try adjusting your search or filters.")) : /* @__PURE__ */ React8.createElement("div", {
    className: "overflow-x-auto rounded-[10px] border border-border bg-surface-1 shadow-xs relative"
  }, isFetching && /* @__PURE__ */ React8.createElement("div", {
    className: "absolute top-0 left-0 right-0 h-0.5 bg-accent-500/20 overflow-hidden z-modal"
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "h-full bg-accent-500 animate-pulse w-full"
  })), /* @__PURE__ */ React8.createElement("table", {
    className: "w-full border-collapse text-left"
  }, /* @__PURE__ */ React8.createElement("thead", null, table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ React8.createElement("tr", {
    key: headerGroup.id,
    className: "bg-surface-2 border-b border-border"
  }, renderExpandedRow && /* @__PURE__ */ React8.createElement("th", {
    className: "w-8 px-2 py-2 border-b border-border sticky left-0 z-20 bg-surface-2"
  }), selectable && /* @__PURE__ */ React8.createElement("th", {
    className: cn7("w-10 sticky left-0 z-20 bg-surface-2 border-b border-border text-center", currentDensity === "comfortable" && "px-3 py-2.5", currentDensity === "compact" && "px-2.5 py-2", currentDensity === "dense" && "px-2 py-1", gridLines && "border-r border-border")
  }, /* @__PURE__ */ React8.createElement("input", {
    type: "checkbox",
    checked: isAllSelected,
    onChange: /* @__PURE__ */ __name((e) => handleSelectAllToggle(e.target.checked), "onChange"),
    className: "rounded border-border text-accent-500 focus:ring-accent-500 h-3.5 w-3.5 cursor-pointer"
  })), headerGroup.headers.map((header, index) => {
    const col = visibleColumns[index];
    const isFirstCol = index === 0;
    return /* @__PURE__ */ React8.createElement("th", {
      key: header.id,
      className: cn7("text-xs font-semibold text-text-secondary uppercase tracking-wider border-b border-border select-none transition-colors", currentDensity === "comfortable" && "px-3.5 py-2.5 text-xs", currentDensity === "compact" && "px-3 py-2 text-[11px]", currentDensity === "dense" && "px-2 py-1 text-[10px] font-bold", gridLines && index < visibleColumns.length - 1 && "border-r border-border", header.column.getCanSort() && "cursor-pointer hover:text-text-primary hover:bg-surface-3/50", isFirstCol && !selectable && !renderExpandedRow && "sticky left-0 z-20 bg-surface-2"),
      onClick: header.column.getToggleSortingHandler(),
      style: {
        width: col?.width,
        minWidth: col?.minWidth ?? col?.width,
        maxWidth: col?.maxWidth
      }
    }, /* @__PURE__ */ React8.createElement("div", {
      className: cn7("flex items-center gap-1.5", col?.numeric ? "justify-end" : "justify-start")
    }, /* @__PURE__ */ React8.createElement("span", null, header.isPlaceholder ? null : header.column.columnDef.header), header.column.getCanSort() && /* @__PURE__ */ React8.createElement("span", {
      className: "text-text-muted flex-shrink-0"
    }, header.column.getIsSorted() === "asc" ? /* @__PURE__ */ React8.createElement(ArrowUp, {
      className: "w-3 h-3 text-accent-500"
    }) : header.column.getIsSorted() === "desc" ? /* @__PURE__ */ React8.createElement(ArrowDown, {
      className: "w-3 h-3 text-accent-500"
    }) : /* @__PURE__ */ React8.createElement(ArrowUpDown, {
      className: "w-3 h-3 opacity-40 hover:opacity-100"
    }))));
  }), hasRowActions && /* @__PURE__ */ React8.createElement("th", {
    className: "w-10 px-2 py-2 border-b border-border sticky right-0 z-20 bg-surface-2 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider"
  }, "Actions")))), /* @__PURE__ */ React8.createElement("tbody", null, table.getRowModel().rows.map((row, rowIdx) => {
    const item = row.original;
    const rowId = getItemId(item, rowIdx);
    const isSelected = selectedIds.includes(rowId);
    const isExpanded = expandedRows.has(rowId);
    return /* @__PURE__ */ React8.createElement(React8.Fragment, {
      key: rowId
    }, /* @__PURE__ */ React8.createElement("tr", {
      className: cn7("border-b border-border last:border-0 transition-colors group", striped && rowIdx % 2 === 1 && "bg-surface-2/30", isSelected ? "bg-accent-50/50 hover:bg-accent-50/70" : "bg-surface-1 hover:bg-surface-2/60", config.onRowClick && "cursor-pointer"),
      onClick: /* @__PURE__ */ __name((e) => {
        const target = e.target;
        if (target.closest('input[type="checkbox"]') || target.closest("a") || target.closest("button")) {
          return;
        }
        config.onRowClick?.(item);
      }, "onClick")
    }, renderExpandedRow && /* @__PURE__ */ React8.createElement("td", {
      className: cn7("w-8 text-center sticky left-0 z-10 transition-colors", isSelected ? "bg-accent-50/50 group-hover:bg-accent-50/70" : "bg-surface-1 group-hover:bg-surface-2/60")
    }, /* @__PURE__ */ React8.createElement("button", {
      type: "button",
      onClick: /* @__PURE__ */ __name(() => toggleRowExpansion(rowId), "onClick"),
      className: "p-1 text-text-secondary hover:text-text-primary rounded hover:bg-surface-3 transition-colors"
    }, /* @__PURE__ */ React8.createElement(ChevronDown, {
      className: cn7("w-3.5 h-3.5 transition-transform duration-150", isExpanded ? "transform rotate-180 text-accent-500" : "")
    }))), selectable && /* @__PURE__ */ React8.createElement("td", {
      className: cn7("w-10 text-center sticky left-0 z-10 transition-colors", isSelected ? "bg-accent-50/50 group-hover:bg-accent-50/70" : "bg-surface-1 group-hover:bg-surface-2/60", currentDensity === "comfortable" && "px-3 py-3", currentDensity === "compact" && "px-2.5 py-2", currentDensity === "dense" && "px-2 py-1", gridLines && "border-r border-border")
    }, /* @__PURE__ */ React8.createElement("input", {
      type: "checkbox",
      checked: isSelected,
      onChange: /* @__PURE__ */ __name((e) => {
        if (!onSelectionChange) return;
        if (e.target.checked) {
          onSelectionChange([
            ...selectedIds,
            rowId
          ]);
        } else {
          onSelectionChange(selectedIds.filter((id) => id !== rowId));
        }
      }, "onChange"),
      className: "rounded border-border text-accent-500 focus:ring-accent-500 h-3.5 w-3.5 cursor-pointer"
    })), visibleColumns.map((col, colIdx) => {
      const isFirstCol = colIdx === 0;
      return /* @__PURE__ */ React8.createElement("td", {
        key: col.id,
        className: cn7("text-text-primary", currentDensity === "comfortable" && "px-3.5 py-3 text-sm", currentDensity === "compact" && "px-3 py-2 text-xs", currentDensity === "dense" && "px-2 py-1 text-xs font-mono tnum", gridLines && colIdx < visibleColumns.length - 1 && "border-r border-border", col.numeric ? "text-right tnum font-medium" : "", isFirstCol && !selectable && !renderExpandedRow && "sticky left-0 z-10 transition-colors bg-surface-1 group-hover:bg-surface-2/60", isFirstCol && !selectable && !renderExpandedRow && isSelected && "bg-accent-50/50 group-hover:bg-accent-50/70"),
        style: {
          width: col.width,
          minWidth: col.minWidth ?? col.width,
          maxWidth: col.maxWidth
        }
      }, renderCell(item, col, customRenderers));
    }), hasRowActions && /* @__PURE__ */ React8.createElement("td", {
      className: cn7("w-10 text-center sticky right-0 z-10 transition-colors", isSelected ? "bg-accent-50/50 group-hover:bg-accent-50/70" : "bg-surface-1 group-hover:bg-surface-2/60", currentDensity === "comfortable" && "px-2 py-2", currentDensity === "compact" && "px-1.5 py-1.5", currentDensity === "dense" && "px-1 py-1")
    }, /* @__PURE__ */ React8.createElement(RowActions, {
      actions: mergedRowActions,
      row: item
    }))), renderExpandedRow && isExpanded && /* @__PURE__ */ React8.createElement("tr", {
      className: "bg-surface-2/40 border-b border-border"
    }, /* @__PURE__ */ React8.createElement("td", {
      colSpan: visibleColumns.length + (selectable ? 1 : 0) + (hasExpandedRow ? 1 : 0) + (hasRowActions ? 1 : 0),
      className: "p-3"
    }, renderExpandedRow(item))));
  })), totals && /* @__PURE__ */ React8.createElement("tfoot", {
    className: "border-t-2 border-border font-bold bg-surface-2 text-text-primary"
  }, /* @__PURE__ */ React8.createElement("tr", null, renderExpandedRow && /* @__PURE__ */ React8.createElement("td", {
    className: "w-8 bg-surface-2"
  }), selectable && /* @__PURE__ */ React8.createElement("td", {
    className: cn7("w-10 sticky left-0 z-sticky bg-surface-2", gridLines && "border-r border-border")
  }), visibleColumns.map((col, colIdx) => {
    const totalVal = totals[col.id];
    const isFirstCol = colIdx === 0;
    return /* @__PURE__ */ React8.createElement("td", {
      key: `total-${col.id}`,
      className: cn7("font-bold text-text-primary", currentDensity === "comfortable" && "px-3.5 py-2.5 text-sm", currentDensity === "compact" && "px-3 py-2 text-xs", currentDensity === "dense" && "px-2 py-1 text-xs tnum font-mono", gridLines && colIdx < visibleColumns.length - 1 && "border-r border-border", col.numeric ? "text-right tnum" : "text-left", isFirstCol && !selectable && !renderExpandedRow && "sticky left-0 z-sticky bg-surface-2")
    }, totalVal !== void 0 ? totalVal : "");
  }), hasRowActions && /* @__PURE__ */ React8.createElement("td", {
    className: "w-10 sticky right-0 z-sticky bg-surface-2"
  }))))), total > 0 && /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-wrap items-center justify-between gap-3 px-1 pt-1 text-xs text-text-secondary"
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "flex items-center gap-3"
  }, /* @__PURE__ */ React8.createElement("span", null, "Showing ", /* @__PURE__ */ React8.createElement("strong", {
    className: "font-semibold text-text-primary tnum"
  }, start), "\u2013", /* @__PURE__ */ React8.createElement("strong", {
    className: "font-semibold text-text-primary tnum"
  }, end), " of", " ", /* @__PURE__ */ React8.createElement("strong", {
    className: "font-semibold text-text-primary tnum"
  }, total), " items"), onLimitChange && /* @__PURE__ */ React8.createElement("div", {
    className: "flex items-center gap-1.5 border-l border-border pl-3"
  }, /* @__PURE__ */ React8.createElement("span", null, "Per page:"), /* @__PURE__ */ React8.createElement("select", {
    value: limit,
    onChange: /* @__PURE__ */ __name((e) => onLimitChange(Number(e.target.value)), "onChange"),
    className: "h-7 px-2 bg-surface-1 border border-border rounded-[6px] text-xs font-medium text-text-primary focus:outline-none focus:border-accent-500"
  }, pageSizeOptions.map((opt) => /* @__PURE__ */ React8.createElement("option", {
    key: opt,
    value: opt
  }, opt))))), /* @__PURE__ */ React8.createElement("div", {
    className: "flex items-center gap-1"
  }, /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPage(1), "onClick"),
    disabled: page <= 1,
    className: "inline-flex items-center justify-center h-7 w-7 rounded-[6px] bg-surface-1 border border-border text-text-secondary hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
    title: "First Page"
  }, /* @__PURE__ */ React8.createElement(ChevronsLeft, {
    className: "w-3.5 h-3.5"
  })), /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPage(page - 1), "onClick"),
    disabled: page <= 1,
    className: "inline-flex items-center justify-center h-7 w-7 rounded-[6px] bg-surface-1 border border-border text-text-secondary hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
    title: "Previous Page"
  }, /* @__PURE__ */ React8.createElement(ChevronLeft, {
    className: "w-3.5 h-3.5"
  })), /* @__PURE__ */ React8.createElement("span", {
    className: "px-2 font-medium text-text-primary tnum"
  }, "Page ", page, " of ", totalPages), /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPage(page + 1), "onClick"),
    disabled: page >= totalPages,
    className: "inline-flex items-center justify-center h-7 w-7 rounded-[6px] bg-surface-1 border border-border text-text-secondary hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
    title: "Next Page"
  }, /* @__PURE__ */ React8.createElement(ChevronRight, {
    className: "w-3.5 h-3.5"
  })), /* @__PURE__ */ React8.createElement("button", {
    type: "button",
    onClick: /* @__PURE__ */ __name(() => setPage(totalPages), "onClick"),
    disabled: page >= totalPages,
    className: "inline-flex items-center justify-center h-7 w-7 rounded-[6px] bg-surface-1 border border-border text-text-secondary hover:bg-surface-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors",
    title: "Last Page"
  }, /* @__PURE__ */ React8.createElement(ChevronsRight, {
    className: "w-3.5 h-3.5"
  })))));
}
__name(DataTable, "DataTable");
function TableSkeleton({ columns }) {
  return /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-col gap-3 animate-pulse"
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "h-10 bg-surface-2 rounded-[10px] border border-border"
  }), /* @__PURE__ */ React8.createElement("div", {
    className: "flex flex-col rounded-[10px] border border-border overflow-hidden bg-surface-1"
  }, /* @__PURE__ */ React8.createElement("div", {
    className: "flex gap-4 px-4 py-2.5 bg-surface-2 border-b border-border"
  }, Array.from({
    length: columns
  }).map((_, i) => /* @__PURE__ */ React8.createElement("div", {
    key: i,
    className: "h-4 flex-1 rounded bg-surface-3"
  }))), Array.from({
    length: 6
  }).map((_, rowIdx) => /* @__PURE__ */ React8.createElement("div", {
    key: rowIdx,
    className: "flex gap-4 px-4 py-3 border-b border-border last:border-0"
  }, Array.from({
    length: columns
  }).map((_2, colIdx) => /* @__PURE__ */ React8.createElement("div", {
    key: colIdx,
    className: "h-4 flex-1 rounded bg-surface-3"
  }))))));
}
__name(TableSkeleton, "TableSkeleton");

// src/use-list-state.ts
function createUrlAdapter(searchParams, navigate) {
  return {
    readParams: /* @__PURE__ */ __name(() => searchParams, "readParams"),
    writeParams: /* @__PURE__ */ __name((params) => {
      navigate({
        search: /* @__PURE__ */ __name((prev) => {
          const next = {
            ...prev
          };
          for (const key of Object.keys(next)) {
            if (!(key in params) || params[key] === void 0 || params[key] === "") {
              delete next[key];
            }
          }
          for (const [key, val] of Object.entries(params)) {
            if (val !== void 0 && val !== "") {
              next[key] = val;
            } else {
              delete next[key];
            }
          }
          return next;
        }, "search"),
        replace: true,
        resetScroll: false
      });
    }, "writeParams")
  };
}
__name(createUrlAdapter, "createUrlAdapter");
export {
  BulkToolbar,
  ColumnVisibilityMenu,
  DataTable,
  FilterBar,
  FilterChips,
  RowActions,
  SearchInput,
  createUrlAdapter,
  exportToCsv,
  getCellValue,
  renderCell
};
//# sourceMappingURL=index.js.map